/**
 * KpiCard - Composant pour afficher un KPI ou une jauge
 * 
 * Affiche une valeur principale avec tendance et comparaison optionnelles.
 */

import React from "react";
import { Typography } from "@/components/ui";
import { FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";
import type { KpiData, KpiVisualConfig, GaugeVisualConfig } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

interface KpiCardProps {
  data: KpiData;
  visual: KpiVisualConfig | GaugeVisualConfig;
  isGauge?: boolean;
}

// Type guard for GaugeVisualConfig
function isGaugeVisualConfig(visual: KpiVisualConfig | GaugeVisualConfig): visual is GaugeVisualConfig {
  return 'min' in visual || 'max' in visual || 'thresholds' in visual;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Formate une valeur selon la configuration
 */
function formatValue(
  value: number,
  format?: KpiVisualConfig["format"] | GaugeVisualConfig["format"]
): string {
  if (!format) {
    return value.toLocaleString("fr-FR");
  }
  
  const { type, precision = 0, currency = "EUR", prefix = "", suffix = "" } = format;
  
  let formatted: string;
  
  switch (type) {
    case "currency":
      formatted = new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
      break;
      
    case "percentage":
      formatted = new Intl.NumberFormat("fr-FR", {
        style: "percent",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value / 100);
      break;
      
    default:
      formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
  }
  
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Retourne l'icône et la couleur de tendance
 */
function getTrendInfo(trend: "up" | "down" | "neutral") {
  switch (trend) {
    case "up":
      return { icon: FaArrowUp, color: "text-success", bgColor: "bg-success/10" };
    case "down":
      return { icon: FaArrowDown, color: "text-error", bgColor: "bg-error/10" };
    default:
      return { icon: FaMinus, color: "text-content-tertiary", bgColor: "bg-arc-surface-secondary" };
  }
}

// ============================================================================
// Sub-components
// ============================================================================

interface TrendBadgeProps {
  change: KpiData["change"];
}

const TrendBadge: React.FC<TrendBadgeProps> = ({ change }) => {
  if (!change) return null;
  
  const { icon: Icon, color, bgColor } = getTrendInfo(change.trend);
  const percentage = Math.abs(change.percentage);
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${bgColor}`}>
      <Icon size={12} className={color} />
      <Typography variant="text-xs" className={`font-medium ${color}`}>
        {percentage.toFixed(1)}%
      </Typography>
    </div>
  );
};

interface ComparisonInfoProps {
  comparison: KpiData["comparison"];
  format?: KpiVisualConfig["format"];
}

const ComparisonInfo: React.FC<ComparisonInfoProps> = ({ comparison, format }) => {
  if (!comparison) return null;
  
  const periodLabels: Record<string, string> = {
    day: "vs hier",
    week: "vs semaine dernière",
    month: "vs mois dernier",
    quarter: "vs trimestre dernier",
    year: "vs année dernière",
  };
  
  return (
    <div className="text-content-tertiary">
      <Typography variant="text-xs">
        {formatValue(comparison.previous, format)} {periodLabels[comparison.period] || comparison.period}
      </Typography>
    </div>
  );
};

// ============================================================================
// Gauge Component
// ============================================================================

interface GaugeProps {
  value: number;
  min?: number;
  max?: number;
  thresholds?: Array<{ value: number; color: string; label?: string }>;
  colors?: string[];
  showValue?: boolean;
  format?: GaugeVisualConfig["format"];
}

const Gauge: React.FC<GaugeProps> = ({ 
  value, 
  min = 0, 
  max = 100, 
  thresholds, 
  colors,
  showValue = true,
  format,
}) => {
  // Calculer le pourcentage basé sur min/max
  const range = max - min;
  const normalizedValue = Math.min(max, Math.max(min, value));
  const percentage = range > 0 ? ((normalizedValue - min) / range) * 100 : 0;
  const strokeDasharray = `${percentage * 2.51327} ${251.327 - percentage * 2.51327}`;
  
  // Déterminer la couleur basée sur les thresholds ou le pourcentage
  const getColor = () => {
    // Si des thresholds sont définis, les utiliser
    if (thresholds && thresholds.length > 0) {
      // Trier les thresholds par valeur décroissante
      const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
      for (const threshold of sortedThresholds) {
        if (normalizedValue >= threshold.value) {
          return threshold.color;
        }
      }
      // Si la valeur est en dessous de tous les thresholds, utiliser le dernier
      return sortedThresholds[sortedThresholds.length - 1]?.color || "var(--color-error, #EF4444)";
    }
    
    // Si des couleurs custom sont définies, utiliser la première
    if (colors && colors.length > 0) return colors[0];
    
    // Couleurs par défaut basées sur le pourcentage
    if (percentage >= 75) return "var(--color-success, #22C55E)";
    if (percentage >= 50) return "var(--color-warning, #F59E0B)";
    return "var(--color-error, #EF4444)";
  };
  
  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="var(--arc-surface-secondary, #f0f0f0)"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke={getColor()}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      {showValue && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Typography variant="h5" className="font-bold">
            {format ? formatValue(normalizedValue, format) : `${Math.round(percentage)}%`}
          </Typography>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const KpiCard: React.FC<KpiCardProps> = ({
  data,
  visual,
  isGauge = false,
}) => {
  const { value, change, comparison } = data;
  const { title, colors } = visual;
  
  // Si c'est une jauge, utiliser les options de GaugeVisualConfig
  if (isGauge) {
    const gaugeVisual = visual as GaugeVisualConfig;
    const min = gaugeVisual.min ?? 0;
    const max = gaugeVisual.max ?? 100;
    const thresholds = gaugeVisual.thresholds;
    const showValue = gaugeVisual.showValue ?? true;
    const format = gaugeVisual.format;
    
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-4">
        {title && (
          <Typography variant="text-sm" className="text-content-secondary mb-2">
            {title}
          </Typography>
        )}
        <Gauge 
          value={value} 
          min={min}
          max={max}
          thresholds={thresholds}
          colors={colors} 
          showValue={showValue}
          format={format}
        />
        <Typography variant="text-sm" className="text-content-tertiary mt-2">
          {formatValue(value, format)} / {formatValue(max, format)}
        </Typography>
      </div>
    );
  }
  
  // KPI Card standard
  const kpiVisual = visual as KpiVisualConfig;
  const valueSize = kpiVisual.valueSize ?? "medium";
  const showTrend = kpiVisual.showTrend ?? true;
  const showComparison = kpiVisual.showComparison ?? true;
  const format = kpiVisual.format;
  
  // Classes de taille pour la valeur
  const valueSizeClasses = {
    small: "text-2xl",
    medium: "text-4xl",
    large: "text-6xl",
  };
  
  return (
    <div className="h-full w-full flex flex-col justify-center p-4">
      {/* Titre */}
      {title && (
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          {title}
        </Typography>
      )}
      
      {/* Valeur principale */}
      <div className="flex items-baseline gap-3">
        <Typography
          variant="h3"
          className={`font-bold text-content-primary ${valueSizeClasses[valueSize]}`}
        >
          {formatValue(value, format)}
        </Typography>
        
        {/* Badge de tendance */}
        {showTrend && change && (
          <TrendBadge change={change} />
        )}
      </div>
      
      {/* Comparaison */}
      {showComparison && comparison && (
        <div className="mt-2">
          <ComparisonInfo comparison={comparison} format={format} />
        </div>
      )}
    </div>
  );
};

export default KpiCard;
